import { Button } from "@/components/ui/button";
import { TabsDummyButton } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
} from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import { useCallback, useEffect, useRef, useState } from "react";
import { SkillPickerTab } from "./skills-picker-lib";

const SCROLL_AMOUNT_PX = 120;

interface SkillPickerCategoryTabsProps {
  tabs: { key: SkillPickerTab; label: string }[];
  selectedTab: SkillPickerTab;
  onSelectTab: (tab: SkillPickerTab) => void;
}

export const SkillPickerCategoryTabs: React.FC<
  SkillPickerCategoryTabsProps
> = ({ tabs, selectedTab, onSelectTab }) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const element = scrollRef.current;
    if (!element) {
      return;
    }
    setCanScrollLeft(element.scrollLeft > 1);
    setCanScrollRight(
      element.scrollLeft + element.clientWidth < element.scrollWidth - 1,
    );
  }, []);

  useEffect(() => {
    updateScrollState();
    const element = scrollRef.current;
    if (!element) {
      return;
    }

    element.addEventListener("scroll", updateScrollState);
    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(element);

    return () => {
      element.removeEventListener("scroll", updateScrollState);
      resizeObserver.disconnect();
    };
  }, [tabs, updateScrollState]);

  function scrollLeft() {
    scrollRef.current?.scrollBy({
      left: -SCROLL_AMOUNT_PX,
      behavior: "smooth",
    });
  }

  function scrollRight() {
    scrollRef.current?.scrollBy({ left: SCROLL_AMOUNT_PX, behavior: "smooth" });
  }

  return (
    <div className="border-border flex items-center gap-1 border-b pb-1">
      {canScrollLeft && (
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="text-muted-foreground shrink-0"
          aria-label="Scroll categories left"
          onClick={scrollLeft}
        >
          <FontAwesomeIcon icon={faChevronLeft} className="size-3" />
        </Button>
      )}
      <div
        ref={scrollRef}
        className={cn(
          "flex min-w-0 flex-1 gap-1 overflow-x-auto",
          "[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        )}
      >
        {tabs.map((tab) => (
          <TabsDummyButton
            key={tab.key}
            isActive={selectedTab === tab.key}
            onMouseUp={() => onSelectTab(tab.key)}
            className="h-6 shrink-0 px-2 py-1 text-xs whitespace-nowrap"
          >
            {tab.label}
          </TabsDummyButton>
        ))}
      </div>
      {canScrollRight && (
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="text-muted-foreground shrink-0"
          aria-label="Scroll categories right"
          onClick={scrollRight}
        >
          <FontAwesomeIcon icon={faChevronRight} className="size-3" />
        </Button>
      )}
    </div>
  );
};
